
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle, Clipboard, Code, DownloadCloud, ArrowRight, ExternalLink } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { generateSlackManifest } from "./utils/slackManifest";

interface SlackConnectionFormProps {
  webhookUrl: string;
  setWebhookUrl: (url: string) => void;
  slackToken: string;
  setSlackToken: (token: string) => void;
  isLoading: boolean;
  onConnect: () => void;
}

const SlackConnectionForm: React.FC<SlackConnectionFormProps> = ({
  webhookUrl,
  setWebhookUrl,
  slackToken,
  setSlackToken,
  isLoading,
  onConnect
}) => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<string>("template");
  
  const handleCopyTemplate = () => {
    const template = generateSlackManifest();
    
    navigator.clipboard.writeText(template);
    toast({
      title: "Template copied!",
      description: "The verified Slack app manifest has been copied to your clipboard",
    });
  };
  
  const openExternalLink = (e: React.MouseEvent<HTMLAnchorElement>, url: string) => {
    e.preventDefault(); // Prevent default navigation
    window.open(url, '_blank', 'noopener,noreferrer'); // Open in new tab safely
  };
  
  return (
    <div className="space-y-4">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-2 mb-4">
          <TabsTrigger value="template">Quick Setup</TabsTrigger>
          <TabsTrigger value="manual">Manual Setup</TabsTrigger>
        </TabsList>
        
        <TabsContent value="template" className="space-y-4">
          <Alert className="bg-amber-50 border-amber-200">
            <AlertCircle className="h-4 w-4 text-amber-500" />
            <AlertDescription className="text-amber-700">
              Use our pre-configured bot template to quickly set up your Slack integration with minimal manual configuration.
            </AlertDescription>
          </Alert>
          
          <div className="border rounded-md p-4 bg-slate-50">
            <h4 className="font-medium mb-2 flex items-center gap-2">
              <Code className="h-4 w-4" /> VirtuStaff Marketing Bot template
            </h4>
            <p className="text-sm text-muted-foreground mb-4">
              This template configures all the necessary permissions and settings for your Slack bot.
            </p>
            
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                onClick={handleCopyTemplate}
                className="flex items-center gap-2"
              >
                <Clipboard className="h-4 w-4" />
                Copy Template
              </Button>
              
              <Button 
                onClick={() => setActiveTab("manual")}
                variant="ghost"
                className="flex items-center gap-2"
              >
                Continue Setup
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          <div className="space-y-4 mt-4">
            <h4 className="font-medium">How to use this template:</h4>
            <ol className="list-decimal list-inside space-y-2 ml-2 text-sm text-muted-foreground">
              <li>Go to <Button 
                variant="link" 
                className="p-0 h-auto text-primary inline-flex items-center"
                onClick={(e) => openExternalLink(e as any, "https://api.slack.com/apps")}
              >
                Slack API Dashboard <ExternalLink className="ml-1 h-3 w-3" />
              </Button></li>
              <li>Click "Create New App" and select "From an app manifest"</li>
              <li>Select your workspace and click "Next"</li>
              <li>Paste the copied template into the JSON tab and click "Next"</li>
              <li>Review the details and click "Create"</li>
              <li>Continue to the "Manual Setup" tab to complete the connection</li>
            </ol>
          </div>
        </TabsContent>
        
        <TabsContent value="manual" className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="webhook">Slack Webhook URL</Label>
            <Input 
              id="webhook"
              placeholder="https://hooks.slack.com/services/..."
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              You can create a webhook URL in the Slack API dashboard under "Incoming Webhooks".
            </p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="token">Slack Bot Token</Label>
            <Input 
              id="token"
              placeholder="xoxb-..."
              value={slackToken}
              onChange={(e) => setSlackToken(e.target.value)}
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              The Bot User OAuth Token from your Slack App's OAuth & Permissions page.
            </p>
          </div>
        </TabsContent>
      </Tabs>
      
      <Button 
        onClick={onConnect} 
        className="w-full mt-4"
        disabled={isLoading || (!webhookUrl || !slackToken)}
      >
        {isLoading ? "Connecting..." : "Connect to Slack"}
      </Button>
    </div>
  );
};

export default SlackConnectionForm;
